<template>
  <div
    ref="wrapper"
    class="scroll"
    @click="emit('click', $event)"
  >
    <slot />
  </div>
</template>

<script setup>
import {
  nextTick,
  onActivated,
  onBeforeUnmount,
  onMounted,
  ref,
  shallowRef,
  watch
} from 'vue';
import BScroll from 'better-scroll';

defineOptions({name: 'Scroll'});

const props = defineProps({
  data: {
    type: Array,
    default: null
  },
  // 是否触发滚动事件
  probeType: {
    type: Number,
    default: 1
  },
  // 是否监听滚动事件
  listenScroll: {
    type: Boolean,
    default: false
  },
  eventPassthrough: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['click', 'distance', 'scrollStore']);
const wrapper = ref(null);
const scroll = shallowRef(null);

watch(() => props.data, () => {
  nextTick(refresh);
});

onMounted(() => {
  nextTick(initScroll);
});

onActivated(() => {
  nextTick(() => {
    enable();
    refresh();
  });
});

onBeforeUnmount(() => {
  if (scroll.value && typeof scroll.value.destroy === 'function') {
    scroll.value.destroy();
  }
  scroll.value = null;
});

function initScroll () {
  if (!wrapper.value || scroll.value) {
    return;
  }

  scroll.value = new BScroll(wrapper.value, {
    probeType: props.probeType,
    scrollY: true,
    // 开启事件透传时浏览器会保留原生 click，不再让 BetterScroll 合成，避免路由切换后重复触发
    click: !props.eventPassthrough,
    momentum: true,
    bounce: false,
    bounceTime: 700,
    deceleration: 0.001,
    momentumLimitTime: 300,
    momentumLimitDistance: 15,
    eventPassthrough: props.eventPassthrough,
    resizePolling: 60
  });

  if (props.listenScroll) {
    scroll.value.on('scrollStart', () => emit('scrollStore', true));
    scroll.value.on('scroll', position => {
      emit('distance', Math.abs(position.y));
      emit('scrollStore', true);
    });
    scroll.value.on('scrollEnd', () => emit('scrollStore', false));
  }
}

function scrollTo (...args) {
  if (scroll.value) {
    scroll.value.scrollTo(...args);
  }
}

function scrollToElement (...args) {
  if (scroll.value) {
    scroll.value.scrollToElement(...args);
  }
}

function refresh () {
  if (scroll.value) {
    scroll.value.refresh();
  }
}

function enable () {
  if (scroll.value && typeof scroll.value.enable === 'function') {
    scroll.value.enable();
  }
}

function getPosition () {
  return scroll.value
    ? {x: scroll.value.x || 0, y: scroll.value.y || 0}
    : {x: 0, y: 0};
}

defineExpose({scrollTo, scrollToElement, refresh, enable, getPosition});

</script>

<style scoped lang="stylus" rel="stylesheet/stylus">
.scroll
  height 100%
  width 100%
  overflow hidden
  background var(--page-bg)
</style>
